import { useState, useEffect, useCallback } from 'react';
import { assemblyService } from 'services/api';
import styles from './AnexosButton.module.css';

const fmtSize = (b) => !b ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(0)} KB` : `${(b/1048576).toFixed(1)} MB`;

// Botón 📎 + modal para subir/listar/borrar los anexos de una asamblea
// (presupuesto, estados financieros, actas previas...). Los propietarios los
// ven en su panel durante la asamblea.
// soloLectura: membresía en período de gracia (P1) → se pueden ver y descargar, no subir ni borrar.
export default function AnexosButton({ assembly, soloLectura = false }) {
  const [open,    setOpen]    = useState(false);
  const [docs,    setDocs]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');

  const load = useCallback(async () => {
    try { const r = await assemblyService.documents(assembly.id); setDocs(r.data || []); }
    catch (err) { setError(err.response?.data?.message || 'No se pudieron cargar los anexos.'); }
  }, [assembly.id]);

  useEffect(() => { if (open) { setDocs(null); setError(''); load(); } }, [open, load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      await assemblyService.uploadDocument(assembly.id, fd);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo subir el archivo.');
    } finally { setBusy(false); }
  };

  const remove = async (docId) => {
    setBusy(true); setError('');
    try { await assemblyService.deleteDocument(assembly.id, docId); await load(); }
    catch (err) { setError(err.response?.data?.message || 'No se pudo eliminar.'); }
    finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const r = await assemblyService.downloadDocument(assembly.id, d.id);
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = d.original_name; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('No se pudo descargar.'); }
  };

  const n = docs === null ? assembly.documents_count : docs.length;

  return (
    <>
      <button className={`btn btn-sm btn-secondary btn-icon ${n ? styles.botonConAnexos : ''}`} onClick={() => setOpen(true)}
        title={n ? `Anexos (${n})` : 'Anexos de la asamblea'} aria-label="Anexos">
        <i className="fas fa-paperclip" />
      </button>
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className={`modal ${styles.modalAnexos}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className={`fas fa-paperclip ${styles.iconoTitulo}`} />
                Anexos — Asamblea #{assembly.number_assembly}</h3>
              <button className="btn-close" onClick={() => setOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div className={`alert alert-danger ${styles.aviso}`} onClick={() => setError('')}>{error}</div>}

              {!soloLectura && (
                <label className={`btn btn-secondary ${busy ? styles.subirOcupado : styles.subir}`}>
                  {busy ? <><i className="fas fa-spinner fa-spin" /> Subiendo...</> : <><i className="fas fa-upload" /> Subir documento</>}
                  <input type="file" hidden disabled={busy} onChange={upload}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
                </label>
              )}
              <div className={styles.ayuda}>
                Carga aquí los <strong>documentos de interés</strong> para la asamblea: presupuesto anual,
                estados financieros, informe de gestión, acta anterior, proyecto de reforma al reglamento,
                cotizaciones, dictamen del revisor fiscal, hojas de vida de candidatos, planos.
                <br />PDF, Word, Excel o imagen · máx. 15 MB. Los propietarios los consultan en su panel
                durante y después de la asamblea.
              </div>

              {docs === null ? (
                <div className={styles.cargando}><div className={`spinner ${styles.spinnerCarga}`} /></div>
              ) : docs.length === 0 ? (
                <div className={styles.sinAnexos}>
                  Aún no hay anexos en esta asamblea.
                </div>
              ) : (
                <div className={styles.lista}>
                  {docs.map(d => (
                    <div key={d.id} className={styles.documento}>
                      <i className={`fas fa-file ${styles.iconoDocumento}`} />
                      <div className={styles.documentoTexto}>
                        <div className={styles.documentoNombre}>{d.name}</div>
                        <div className={styles.documentoTamano}>{fmtSize(d.size)}</div>
                      </div>
                      <button className="btn btn-sm btn-secondary btn-icon" title="Descargar" onClick={() => download(d)}>
                        <i className="fas fa-download" />
                      </button>
                      {!soloLectura && (
                        <button className="btn btn-sm btn-danger btn-icon" title="Eliminar" disabled={busy} onClick={() => remove(d.id)}>
                          <i className="fas fa-trash" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
